import { withRentalProductOffer } from '../../utilities/productStructuredData'
import React from 'react'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { BreadcrumbJsonLd, LocalBusinessJsonLd, NextSeo } from 'next-seo'
import { useDispatch, useSelector } from 'react-redux'
import { FiLayers } from '@react-icons/all-files/fi/FiLayers'
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle'
import { FiMonitor } from '@react-icons/all-files/fi/FiMonitor'
import { FiUsers } from '@react-icons/all-files/fi/FiUsers'
import { wrapper } from '../../store/configStore'
import { SERVER_IP } from '../../store/middleware/api'
import { getFilteredProducts, productsReceived, productsSetFilters } from '../../store/slices/entities/products'
import { getActiveUser } from '../../store/slices/user'
import { SHOW_POPUP } from '../../store/slices/ui'
import { BUSINESS_HOURS } from '../../content/businessHours'
import CatalogHelp from '../../components/design-system/commerce/CatalogHelp'
import ProductCatalog from '../../components/design-system/commerce/ProductCatalog'
import Container from '../../components/design-system/foundations/Container'
import PageShell from '../../components/design-system/foundations/PageShell'
import ClosingActionBand from '../../components/design-system/marketing/ClosingActionBand'
import EquipmentGuide from '../../components/design-system/marketing/EquipmentGuide'
import EventShowcase from '../../components/design-system/marketing/EventShowcase'
import FAQGroup from '../../components/design-system/marketing/FAQGroup'
import MediaFeature from '../../components/design-system/marketing/MediaFeature'
import RelatedServices from '../../components/design-system/marketing/RelatedServices'
import ReviewProof from '../../components/design-system/marketing/ReviewProof'
import ServiceCategoryGrid from '../../components/design-system/marketing/ServiceCategoryGrid'
import ServiceHero from '../../components/design-system/marketing/ServiceHero'
import ServiceIntroduction from '../../components/design-system/marketing/ServiceIntroduction'
import VisualDecisionGuide from '../../components/design-system/marketing/VisualDecisionGuide'
import QuotePop from '../../components/DeferredQuotePopup'
import * as gtag from '../../lib/gtag'
import { compactCatalogProducts } from '../../utilities/catalogPublicPayload'
import { compactPublicEvents } from '../../utilities/eventPublicPayload'
import { getVisualFamilyBreadcrumbs, toBreadcrumbJsonLdItems, toHeroBreadcrumbs } from '../../utilities/visualFamilyBreadcrumbs'
import { isProductAvailableInMarket, MARKET_ORLANDO } from '../../utilities/fulfillmentMarket'
import { requestTvPageData } from '../../utilities/tvPageData'
import tvSportsSetup from '../../public/Pictures/tvs-orlando.jpg'
import portraitDisplays from '../../public/Pictures/tv-b.jpg'
import presentationDisplay from '../../public/Pictures/tv-c.jpg'

const SeoPageDrawer = dynamic(() => import('../../components/SeoPageDrawer'), { ssr: false })

const PAGE_PATH = '/orlando/tv-rental'
const CANONICAL = `https://miamisoundrental.com${PAGE_PATH}`
const TITLE = 'TV Rental Orlando | TV + Stand Packages & Setup'
const DESCRIPTION = 'Rent complete TV-and-stand packages in Orlando with 43–85″ 4K screens, portrait or landscape mounting, touchscreens, pickup, or delivery and setup.'
const ORLANDO_REVIEWS_URL = 'https://www.google.com/maps/search/?api=1&query=Miami+Sound+Rental+Orlando+FL'

const questions = [
  {
    question: 'Can I pick up a TV from your Orlando warehouse?',
    answer: 'Yes. Eligible TV-and-stand packages can be picked up from our Orlando branch at 8600 Commodity Circle. Choose the dates first to see live Orlando availability and the fulfillment options available for each complete setup.'
  },
  {
    question: 'Do you deliver and set up TV rentals in Orlando?',
    answer: 'Yes. Delivery and setup are available across Orlando and Central Florida. Enter the dates and venue to receive the applicable delivery, stand, setup, and collection price before checkout.'
  },
  {
    question: 'Can you deliver TV rentals to Tampa?',
    answer: 'Yes. Tampa TV rentals are fulfilled by our Orlando team when the equipment and travel window are available. Enter the Tampa address during booking so fulfillment and travel pricing can be confirmed.'
  },
  {
    question: 'What TV sizes can I rent in Orlando?',
    answer: 'Complete TV packages commonly use 43, 50, 55, 65, 75, and 85-inch displays, subject to live Orlando inventory. Choose the screen within the setup based on viewing distance, audience, content, orientation, and available floor space.'
  },
  {
    question: 'Do TV rentals include a stand?',
    answer: 'Yes. TV rentals are offered as complete display setups rather than bare screens. Each package identifies the included stand or mounting method, and the screen list is provided only to compare compatible sizes and models within a complete setup.'
  },
  {
    question: 'Can a TV be mounted vertically for portrait content?',
    answer: 'Many displays can be configured in portrait orientation for digital signage, brand activations, and wayfinding. Confirm the specific model, stand, content format, and venue clearance with our team before the event.'
  },
  {
    question: 'Should I rent a TV, projector, or LED video wall?',
    answer: 'Use a TV for close-range indoor viewing, signage, meetings, exhibits, and smaller presentation areas. A projector is useful for a large image in a controlled-light room. Choose an LED video wall for very large formats, long viewing distances, bright environments, or outdoor applications.'
  },
  {
    question: 'Can I rent a TV for one day or on short notice?',
    answer: 'Yes, one-day and short-term TV-and-stand packages are available when inventory permits. Same-day service may also be possible. Online bookings require at least four hours of notice, including two weekday business hours; call 407 807 0052 for shorter-notice requests.'
  }
]

const productTerms = product => [
  product?.name,
  product?.description,
  ...(product?.tags || [])
].filter(Boolean).join(' ').toLowerCase()

const isTvProduct = product => product?.categories?.includes('Visuals/TVs')

const isCompleteDisplayPackage = product => {
  const name = String(product?.name || '').toLowerCase()
  const nameAndTags = [name, ...(product?.tags || [])].join(' ').toLowerCase()
  const hasScreenSize = /\b\d{2}\s*(?:"|inches?\b)/.test(name)
  const describesScreen = /\b(television|monitor|display|screen)\b/.test(name)
    || /\btv\b/.test(name)
  const describesSupport = /\b(stand|mount|mounted|truss)\b/.test(name)
  return Boolean(product?.bundle)
    || /\b(bundle|package)\b/.test(nameAndTags)
    || (hasScreenSize && describesScreen && describesSupport)
}

const isDisplayAccessory = product => {
  if (isCompleteDisplayPackage(product)) return false
  const nameAndTags = [product?.name, ...(product?.tags || [])].filter(Boolean).join(' ').toLowerCase()
  return /\b(stand|mount|truss|rig|shelf|remote|cable|adapter|case)\b/.test(nameAndTags)
}

const absoluteProductUrl = value => {
  if (!value) return CANONICAL
  try {
    return new URL(value, 'https://miamisoundrental.com').toString()
  } catch (_) {
    return CANONICAL
  }
}

export const getStaticProps = wrapper.getStaticProps(async ({ store }) => {
  const [landingResponse, productResponse, reviewResponse] = await Promise.all([
    requestTvPageData({
      url: `${SERVER_IP}/user/static-landing`,
      fallback: { newsletterevents: [], total_events: null },
      label: `landing proof for ${PAGE_PATH}`
    }),
    requestTvPageData({
      url: `${SERVER_IP}/inventory/product`,
      params: { msr: true, cat: 'tvs' },
      required: true,
      validateData: Array.isArray,
      label: `products for ${PAGE_PATH}`
    }),
    requestTvPageData({
      url: `${SERVER_IP}/google-review/public`,
      params: { page: PAGE_PATH, limit: 3 },
      fallback: null,
      label: `reviews for ${PAGE_PATH}`
    })
  ])

  const products = Array.isArray(productResponse.data) ? productResponse.data : []
  store.dispatch({ type: productsReceived.type, payload: compactCatalogProducts(products) })
  store.dispatch({ type: productsSetFilters.type, payload: { static: true } })

  const { newsletterevents = [], total_events = null } = landingResponse.data || {}
  const usedFallback = [landingResponse, productResponse, reviewResponse]
    .some(response => response.usedFallback)

  return {
    props: {
      newsletterevents: compactPublicEvents(newsletterevents),
      total_events,
      reviewData: reviewResponse.data
    },
    revalidate: usedFallback ? 60 : 3600
  }
})

const OrlandoTvRental = ({ newsletterevents, total_events, reviewData }) => {
  const products = useSelector(getFilteredProducts)
  const user = useSelector(getActiveUser)
  const dispatch = useDispatch()
  const breadcrumbs = getVisualFamilyBreadcrumbs(PAGE_PATH)
  const tvProducts = products.filter(isTvProduct)
  const displayPackages = tvProducts.filter(isCompleteDisplayPackage)
  const displayAccessories = tvProducts.filter(isDisplayAccessory)
  const individualDisplays = tvProducts.filter(product => (
    !isCompleteDisplayPackage(product) && !isDisplayAccessory(product)
  ))
  const localSchemaProducts = displayPackages.filter(product => (
    isProductAvailableInMarket(product, MARKET_ORLANDO)
  ))

  const openQuote = () => {
    gtag.event({ action: 'quote_click', label: 'leads', value: 3, category: 'orders' })
    dispatch(SHOW_POPUP({ key: 'quote', size: 'medium', body: <QuotePop /> }))
  }

  const trackCall = () => gtag.event({
    action: 'booking_assistance_call_CTA',
    label: 'leads',
    value: 2,
    category: 'orders'
  })

  const primaryAction = {
    label: 'Browse TV + Stand Packages',
    href: '#orlando-tv-catalog',
    icon: FiMonitor,
    conversionKey: 'orlando-tv-rental.hero.browse-packages',
    channel: 'rental',
    salesPath: 'self_service',
    onClick: () => gtag.event({ action: 'catalog_click', label: 'leads', value: 2, category: 'orders' })
  }

  const secondaryAction = {
    label: 'Plan a Display Setup',
    icon: FiMessageCircle,
    conversionKey: 'orlando-tv-rental.hero.plan-display',
    channel: 'quote',
    salesPath: 'managed',
    onClick: openQuote
  }

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${CANONICAL}#service`,
    name: 'TV Rental in Orlando and Central Florida',
    serviceType: 'Complete 4K TV-and-stand packages, touchscreens, mounted displays, and multi-display event rental',
    url: CANONICAL,
    description: DESCRIPTION,
    provider: { '@id': 'https://miamisoundrental.com/orlando#localbusiness' },
    areaServed: [
      { '@type': 'City', name: 'Orlando' },
      { '@type': 'City', name: 'Tampa' },
      { '@type': 'AdministrativeArea', name: 'Central Florida' }
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Orlando TV-and-stand rental packages',
      itemListElement: (localSchemaProducts.length ? localSchemaProducts : displayPackages)
        .slice(0, 12)
        .map(product => withRentalProductOffer({
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Product',
            name: product.name,
            description: String(product.description || '').replace(/<[^>]*>/g, ' ').trim()
          },
          priceCurrency: 'USD',
          price: Number(product.price?.rental || 0),
          availability: 'https://schema.org/InStock',
          url: absoluteProductUrl(product.url)
        })).filter(Boolean)
    }
  }

  const decisionPaths = [
    {
      label: 'Primary rental path',
      title: 'TV-and-stand package',
      icon: FiUsers,
      summary: 'Start with a configured package so the screen, compatible stand or mount, and common event accessories are reserved together.',
      points: ['Stand or truss mounting identified', 'Package contents shown before booking', 'Pickup or delivery selected after dates'],
      href: '#orlando-tv-catalog',
      actionLabel: 'Compare TV packages'
    },
    {
      label: 'Screen selection inside the setup',
      title: 'Choose the right screen size',
      icon: FiMonitor,
      summary: 'Compare 43–85-inch screens by viewing distance, content, audience, orientation, and floor space after starting with the complete setup.',
      points: ['Not offered as a bare-screen rental', 'Presentations, signage, and playback', 'Landscape or compatible portrait setup'],
      href: '#orlando-tv-screen-options',
      actionLabel: 'Compare screen options'
    },
    {
      label: 'Several rooms or interactive content',
      title: 'Multi-screen or touchscreen plan',
      icon: FiLayers,
      summary: 'Ask for an assisted plan when screens share a source or require longer cable paths, converters, synchronized playback, or touch interaction.',
      points: ['Breakout and overflow rooms', 'Touchscreens and confidence monitors', 'Signal distribution confirmed in advance']
    }
  ]

  const useCases = [
    {
      eyebrow: 'Meetings and watch parties',
      title: 'Clear close-range viewing in one or several rooms',
      summary: 'Flat-screen TVs work well where guests are close enough to read detailed content and the room does not require a very large image.',
      body: <p>Choose the screen within the complete setup by audience size, viewing distance, room layout, and source connection. The pictured setup uses two real event displays on stands for shared sports viewing.</p>,
      image: tvSportsSetup,
      imageAlt: 'Two flat-screen TVs on stands supporting a sports watch party'
    },
    {
      eyebrow: 'Brand activations and signage',
      title: 'Portrait displays for vertical creative',
      summary: 'Compatible screens can be mounted vertically for sponsor content, schedules, menus, wayfinding, and branded installations.',
      body: <p>Confirm the exact screen model, orientation, content resolution, stand, and venue clearance before the event.</p>,
      image: portraitDisplays,
      imageAlt: 'Three portrait flat-screen displays used for branded event content'
    },
    {
      eyebrow: 'Stages and presentation areas',
      title: 'Confidence and support displays near the presenter',
      summary: 'A monitor near the stage can support slides, timers, notes, or confidence viewing without replacing the main audience display.',
      body: <p>When the audience also needs a large image, pair the confidence monitor with a projector or modular LED video wall selected for the room.</p>,
      image: presentationDisplay,
      imageAlt: 'Flat-screen display on a stand beside a small presentation stage'
    }
  ]

  return <>
    <BreadcrumbJsonLd itemListElements={toBreadcrumbJsonLdItems(breadcrumbs)} />
    <NextSeo
      title={TITLE}
      description={DESCRIPTION}
      canonical={CANONICAL}
      twitter={{ handle: '@miamisoundrent1', site: '@miamisoundrent1', cardType: 'summary_large_image' }}
      openGraph={{
        type: 'website',
        url: CANONICAL,
        title: TITLE,
        description: DESCRIPTION,
        images: [{
          url: 'https://miamisoundrental.com/Pictures/tvs-orlando.jpg',
          width: 800,
          height: 600,
          alt: 'Two flat-screen TVs on stands supporting an Orlando event'
        }],
        site_name: 'Miami Sound Rental'
      }}
      additionalMetaTags={[{
        name: 'keywords',
        content: 'TV rental Orlando, rent TV Orlando, flat screen TV rental Orlando, 4K TV rental Orlando, TV rental with stand Orlando, TV rental Tampa'
      }]}
    />
    <LocalBusinessJsonLd
      type='Store'
      id='https://miamisoundrental.com/orlando#localbusiness'
      name='Miami Sound Rental Orlando'
      description='Audio visual equipment rental and event production services from our Orlando branch.'
      url='https://miamisoundrental.com/orlando'
      telephone='+14078070052'
      address={{
        streetAddress: '8600 Commodity Cir #152',
        addressLocality: 'Orlando',
        addressRegion: 'FL',
        postalCode: '32819',
        addressCountry: 'US'
      }}
      geo={{ latitude: '28.445134', longitude: '-81.431191' }}
      images={['https://miamisoundrental.com/Pictures/tvs-orlando.jpg']}
      openingHours={BUSINESS_HOURS}
      areaServed={[{
        geoMidpoint: { latitude: '28.445134', longitude: '-81.431191' },
        geoRadius: '160000'
      }]}
    />
    <Head>
      <script
        key='orlando-tv-rental-service-schema'
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
    </Head>

    <PageShell>
      <ServiceHero
        breadcrumbs={toHeroBreadcrumbs(breadcrumbs)}
        eyebrow='TV rental · Orlando, Tampa & Central Florida'
        title='TV Rental in Orlando'
        description='Start with a complete TV-and-stand package, then choose the screen size and configuration for your event. See Orlando inventory first, then choose warehouse pickup or delivery and setup.'
        image={{ src: tvSportsSetup, position: 'center' }}
        imageAlt='Two flat-screen TVs on professional stands supporting an Orlando event'
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        processLabel='How TV rental works'
        steps={[
          { icon: 'search', title: 'Choose a package', description: 'Start with a complete setup.' },
          { icon: 'calendar', title: 'Select dates', description: 'See live availability.' },
          { icon: 'truck', title: 'Choose fulfillment', description: 'Pickup or delivery and setup.' },
          { icon: 'success', title: 'Confirm online', description: 'Review the final price first.' }
        ]}
        stackActionsOnMobile
      />

      <ServiceIntroduction
        eyebrow='Complete Orlando TV setups'
        heading='Start with a TV-and-stand package—not a bare screen'
        body={<>
          <p>TV rentals on this page are offered as complete display setups. Begin with a package that combines the screen with a compatible stand or mounting method; individual screens are not presented as standalone rentals.</p>
          <p>After choosing the setup, compare 43–85-inch screen options for presentations, meetings, exhibits, lounges, registration, digital signage, and close-range viewing. The catalogs open with Orlando inventory. Select <strong>All</strong> only to include identified Miami-fulfilled options.</p>
        </>}
        guideHeading='Orlando pickup or Central Florida delivery?'
        guideSummary='Choose the fulfillment path that matches the display, venue, and support required.'
        guideBody={<>
          <p>Eligible TV-and-stand packages can be picked up from our Orlando warehouse. Delivery and setup are available across Orlando and Central Florida, including Tampa when the equipment and travel window are available.</p>
          <p>Enter the dates and location to see current availability and the applicable price. For same-day or shorter-notice requests, call <a href='tel:+14078070052'>407 807 0052</a>.</p>
        </>}
        image={portraitDisplays}
        imageAlt='Three portrait flat-screen displays used for a real branded event installation'
        signals={[
          'Orlando inventory is the default catalog view',
          'Complete TV-and-stand packages lead the rental path',
          'Live dates and fulfillment choices appear before checkout'
        ]}
      />

      <VisualDecisionGuide
        id='orlando-tv-selection-guide'
        eyebrow='Display decision guide'
        heading='Start with a complete package, then choose the screen and support level'
        description='The package is the rental product. Screen size, mounting, source, quantity, and signal distance determine which complete setup fits the event.'
        items={decisionPaths}
      />

      {displayPackages.length > 0 && (
        <ProductCatalog
          carousel={{ name: 'Orlando TV Display Packages', url: `${CANONICAL}#orlando-tv-catalog`, description: 'TV packages, touchscreens, stands, and mounted display systems for Orlando events' }}
          filters={[["TV + Stand Packages", 'bundle'], ['Touchscreens', 'touchscreen'], ['Mounted Displays', 'truss']]}
          sortOptions={['Screen Size', 'Screen Type']}
          listName='Orlando TV + Stand Packages'
          anchorId='orlando-tv-catalog'
          eyebrow='Primary rental options'
          description='Choose a complete TV-and-stand package, touchscreen, or mounted display system. These configured setups—not bare screens—are the starting point for an Orlando TV rental.'
          products={displayPackages}
          inventoryMarket='Orlando'
        />
      )}

      {individualDisplays.length > 0 && (
        <ProductCatalog
          actionMode='details'
          filters={[
            ['43 inch', '43'],
            ['50 inch', '50'],
            ['55 inch', '55'],
            ['65 inch', '65'],
            ['75 inch', '75'],
            ['85 inch', '85']
          ]}
          sortOptions={['Screen Size', 'Screen Type']}
          listName='Screen Sizes for Complete TV Setups'
          anchorId='orlando-tv-screen-options'
          eyebrow='Secondary screen reference'
          description='Compare screens stocked in Orlando after selecting a complete TV-and-stand or mounted-display setup. These screens are not offered here as standalone rentals; select All only to include identified Miami-fulfilled options.'
          products={individualDisplays}
          inventoryMarket='Orlando'
          supportingLabel='Screen option within a complete setup'
          supportingDescription='This screen is selected as part of a TV-and-stand package or another configured display setup. It is not offered here as a bare-screen rental.'
          supportingActionLabel='View screen specifications'
        />
      )}

      {displayAccessories.length > 0 && (
        <ProductCatalog
          listName='TV Stands and Display Accessories'
          anchorId='orlando-tv-accessories'
          eyebrow='Complete the display setup'
          description='Add the stand, mount, shelf, cable, adapter, or related accessory required by the selected screen and source.'
          products={displayAccessories}
          showAssurances={false}
          showProductDirectory={false}
          pageSize={6}
          inventoryMarket='Orlando'
        />
      )}

      <Container>
        <CatalogHelp onQuote={openQuote} onCall={trackCall} phone='407 807 0052' phoneHref='tel:+14078070052' />
      </Container>

      <VisualDecisionGuide
        id='orlando-display-format-guide'
        eyebrow='Choose the correct display format'
        heading='TV, projector, or LED video wall?'
        description='These services solve different viewing problems. This page owns close-range TV and monitor demand—not projector or modular LED display searches.'
        density='compact'
        items={[
          { label: 'Close-range indoor viewing', title: 'TV or monitor', icon: FiMonitor, summary: 'Best for meetings, signage, exhibits, lounges, and smaller presentation areas where viewers are relatively close.' },
          { label: 'Large image in controlled light', title: 'Projector and screen', icon: FiUsers, summary: 'Useful when a larger image is needed indoors and ambient light can be managed.', href: '/orlando/projector-rental', actionLabel: 'Compare Orlando Projectors' },
          { label: 'Very large, bright, or outdoor format', title: 'LED video wall', icon: FiLayers, summary: 'Choose modular LED for custom dimensions, bright venues, outdoor events, or long viewing distances.', href: '/orlando/led-video-wall-rental', actionLabel: 'Plan an Orlando LED Wall' }
        ]}
      />

      <MediaFeature
        eyebrow='Multi-display planning'
        heading='Several screens may also require a signal-distribution plan'
        body={<>
          <p>Multiple TVs do not automatically share content. The complete setup may need an HDMI distribution amplifier, converters, extenders, playback computers, longer cable routes, or synchronized sources.</p>
          <p>Tell us how many screens need the same or different content, where the source will be located, and whether any display is being used as a confidence monitor.</p>
        </>}
        secondaryHeading='Confirm sources and connections before the event'
        secondaryBody={<p>Laptops, media players, cameras, streaming devices, and presentation switchers can require different adapters or protected-content support. The assisted path is safer when the source or distribution method is uncertain.</p>}
        image={presentationDisplay}
        imageAlt='Flat-screen monitor positioned beside a presentation stage with a connected signal source'
        reverse
      />

      <EquipmentGuide
        eyebrow='Real display applications'
        title='How event teams use TV rentals'
        description='Actual Miami Sound Rental display installations show the different roles a TV or monitor can play; they are not presented as Orlando-only projects.'
        headingId='orlando-tv-use-cases'
        items={useCases}
      />

      <RelatedServices
        eyebrow='Related Orlando visual services'
        heading='Choose the display system that fits the venue'
        description='Keep close-range TVs and monitors here, or move to the specialist page when the event needs projection or a modular LED wall.'
        headingId='orlando-tv-related-services'
        additionalServices={[
          { h1: 'Orlando LED Video Wall Rental', url: '/orlando/led-video-wall-rental', miniText: 'Plan a brighter, custom-size modular display for large audiences, long viewing distances, or outdoor use.' },
          { h1: 'Orlando Projector Rental', url: '/orlando/projector-rental', miniText: 'Compare projectors and screens for larger indoor images in controlled-light rooms.' },
          { h1: 'Orlando AV Equipment Rental', url: '/orlando', miniText: 'Return to the Orlando hub for audio, DJ equipment, karaoke, staging, and other event-rental categories.' }
        ]}
      />

      <FAQGroup
        questions={questions}
        title='Orlando TV Rental Questions'
        headingId='orlando-tv-rental-faq-title'
        intro='Answers about Orlando inventory, Tampa and Central Florida delivery, TV sizes, stands, orientation, timing, and choosing the right display format.'
      />
      <ServiceCategoryGrid orlando currentCategory='visuals' description='Explore categories with dedicated Orlando pages, or use the Orlando hub for other equipment available from the Central Florida branch.' />
      <EventShowcase
        eyebrow='Company-wide recent production work'
        heading='Displays working in real event environments'
        description='These documented Miami Sound Rental projects show the kinds of rooms, audiences, and content our teams support; they are not presented as Orlando-only events.'
        filter={['corporate', 'conference', 'trade show', 'presentation', 'private']}
        events={newsletterevents}
        totalEvents={total_events}
        sectionId='orlando-tv-production-proof'
      />
      <ReviewProof
        filter={['visuals', 'corporate', 'party']}
        heading='Recent feedback relevant to displays and event service'
        description='Showing verified Google feedback selected for visual-equipment, event-service, and Orlando relevance when available.'
        initialData={reviewData}
        pagePath={PAGE_PATH}
        reviewsUrl={ORLANDO_REVIEWS_URL}
        sectionId='orlando-tv-client-reviews'
      />
      <ClosingActionBand
        ariaLabel='Orlando TV rental options'
        eyebrow='Choose your next step with confidence'
        heading='Ready to reserve the right display?'
        headingId='orlando-tv-closing-action-heading'
        description='Compare complete Orlando TV-and-stand packages online, or ask a visual specialist to help with screen size, mounting, sources, delivery, and multi-screen distribution.'
        proofItems={['Complete TV setups shown first', 'Bare screens are not presented as rentals', 'Fulfillment pricing confirmed before checkout']}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
      />
    </PageShell>

    {user?.email === 'pedro@drynwet.com' && user?.employee && <SeoPageDrawer pagePath={PAGE_PATH} />}
  </>
}

export default OrlandoTvRental
